import {
  afterNextRender,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  Injector,
  input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  output,
  signal,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LookupVTO } from '../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { LookupService } from '../../../../services/pharmacy/lookup.service';
import { ApprovedPharmaciesService } from '../../../../services/pharmacy/approved-pharmacies.service';
import { Loading } from '../../../../utils/loading/loading';
import { CancelIcon } from '../../../../shared/ui/icons/cancel-icon/cancel-icon';
import { SubmitButton } from '../../../../shared/ui/Buttons/submit-button/submit-button';
import { Error } from '../../../public-user-component/auth/shared/error/error';

@Component({
  selector: 'app-follow-up-dialog',
  imports: [Loading, CancelIcon, SubmitButton, Error],
  templateUrl: './follow-up-dialog.html',
  styleUrl: './follow-up-dialog.scss',
})
export class FollowUpDialog implements OnInit, OnDestroy {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private approvedPharmaciesService = inject(ApprovedPharmaciesService);
  private destroyRef = inject(DestroyRef);
  private injector = inject(Injector);
  private host = inject<ElementRef<HTMLElement>>(ElementRef);
  private document = inject(DOCUMENT);
  private previousFocus = this.document.activeElement as HTMLElement | null;

  pharmacyId = input.required<number>();

  closed = output<void>();
  saved = output<void>();

  reasons = signal<LookupVTO[]>([]);
  loading = signal(true);
  loadError = signal(false);
  selectedIndex = signal(0);
  comment = signal('');
  submitting = signal(false);
  errorMessage = signal('');

  isArabic() {
    return this.locale.startsWith('ar');
  }

  ngOnInit(): void {
    this.lookupService
      .getNegativeOrderReasons()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.reasons.set(res.list);
          this.loading.set(false);
          afterNextRender(() => this.focusOption(0), { injector: this.injector });
        },
        error: () => {
          this.loadError.set(true);
          this.loading.set(false);
        },
      });
  }

  ngOnDestroy(): void {
    this.previousFocus?.focus();
  }

  onKeydown(event: KeyboardEvent) {
    event.stopPropagation();

    if (event.key === 'Escape') {
      this.close();
      return;
    }
    if ((event.target as HTMLElement).tagName === 'TEXTAREA') return;

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      const last = this.reasons().length - 1;
      const next = Math.min(last, Math.max(0, this.selectedIndex() + (event.key === 'ArrowDown' ? 1 : -1)));
      this.focusOption(next);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      this.save();
    }
  }

  focusOption(index: number) {
    if (!this.reasons().length) return;
    this.selectedIndex.set(index);
    this.host.nativeElement.querySelectorAll<HTMLElement>('[role="radio"]')[index]?.focus();
  }

  save() {
    const reason = this.reasons()[this.selectedIndex()];
    if (!reason || this.submitting()) return;

    this.submitting.set(true);
    this.errorMessage.set('');
    this.approvedPharmaciesService
      .setNegativeOrderReason(this.pharmacyId(), reason.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.submitting.set(false);
          this.saved.emit();
        },
        error: (error) => {
          this.submitting.set(false);
          this.errorMessage.set(
            (this.isArabic() ? error?.error?.messageAr : error?.error?.messageEn) ||
              (this.isArabic() ? 'حدث خطأ أثناء الحفظ' : 'An error occurred while saving'),
          );
        },
      });
  }

  close() {
    this.closed.emit();
  }
}
